import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Play, X, Radio } from 'lucide-react';
import { useTwitchChannel } from '../hooks/useTwitch';

const DISMISS_KEY = 'jdv-live-now-dismissed-stream';

export default function LiveNowFloat() {
  const { data, live } = useTwitchChannel();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissedStreamId, setDismissedStreamId] = useState(
    () => localStorage.getItem(DISMISS_KEY) || ''
  );

  const stream = data?.stream;
  const streamId = stream?.id || '';

  useEffect(() => {
    if (!live?.isLive) return undefined;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [live?.isLive]);

  if (!live?.isLive) return null;
  if (streamId && streamId === dismissedStreamId) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (streamId) {
      localStorage.setItem(DISMISS_KEY, streamId);
      setDismissedStreamId(streamId);
    } else {
      setDismissedStreamId('manual');
    }
  };

  const onCardClick = (e) => {
    e.preventDefault();
    navigate('/');
    // After nav, scroll Hero into view.
    setTimeout(() => {
      const hero = document.querySelector('#home') || document.body;
      hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}\u2026` : s);

  return (
    <div
      className={`fixed right-4 z-40 transition-all duration-500 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      style={{ bottom: 'calc(1rem + var(--follower-ticker-h, 0px))' }}
      data-testid="live-now-float"
    >
      <a
        href="/"
        onClick={onCardClick}
        className="group flex items-stretch gap-3 max-w-[19rem] rounded-xl border border-red-500/40 bg-zinc-950/90 backdrop-blur-md shadow-2xl shadow-red-900/30 hover:border-red-400/70 transition-colors overflow-hidden pr-9"
      >
        <div className="shrink-0 w-2 bg-gradient-to-b from-red-500 to-rose-600 animate-pulse" />

        <div className="flex-1 py-3 pr-2 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-red-300">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500" />
            </span>
            LIVE NU · Twitch
          </div>
          <p className="text-white font-semibold leading-snug mt-1 line-clamp-2 group-hover:text-red-200 transition-colors">
            {truncate(live.title, 60)}
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-400">
            <span className="inline-flex items-center gap-1">
              <Radio className="w-3 h-3 text-red-300" />
              <span className="text-zinc-200">{truncate(live.category, 22)}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span className="text-zinc-200 font-semibold">
                {Number(live.viewers).toLocaleString('nl-NL')}
              </span>
            </span>
          </div>
          <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-red-600 group-hover:bg-red-500 text-white text-xs font-semibold transition-colors">
            <Play className="w-3 h-3 fill-current" /> Kijk nu
          </div>
        </div>
      </a>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Sluiten"
        data-testid="live-now-dismiss"
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
